import type { Role, User } from './types';

/** Must match backend/src/Aperture.Domain/Identity/Role.cs enum order exactly - SuperAdmin is the only value left. */
const ROLE_BY_INDEX: Role[] = ['SuperAdmin'];

interface RawUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: number | string;
  roles?: (number | string)[];
  organizationId?: string | null;
  isActive?: boolean;
  createdAt?: string;
}

function roleToName(role: number | string | undefined): Role | null {
  if (role === undefined || role === null) return null;
  if (typeof role === 'string') return (ROLE_BY_INDEX as string[]).includes(role) ? (role as Role) : null;
  return ROLE_BY_INDEX[role] ?? null;
}

/**
 * The backend now models each user with a `roles` collection
 * (Aperture.Application.Identity.Dtos.UserDto.Roles - a user can hold several
 * roles at once, e.g. Judge and Competitor). It serializes as raw numeric
 * ordinals (System.Text.Json default, no JsonStringEnumConverter), same as
 * every other enum on this backend - so each entry needs roleToName() too,
 * not just a direct cast. `role` (singular) is kept as a fallback for any
 * older cached response shape.
 */
export function normalizeUser(raw: RawUser): User {
  const roles: Role[] =
    Array.isArray(raw.roles) && raw.roles.length > 0
      ? raw.roles.map(roleToName).filter((r): r is Role => r !== null)
      : [roleToName(raw.role)].filter((r): r is Role => r !== null);

  const displayName =
    raw.firstName || raw.lastName ? `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim() : undefined;

  return {
    id: raw.id,
    email: raw.email,
    firstName: raw.firstName,
    lastName: raw.lastName,
    displayName,
    roles,
    organizationId: raw.organizationId,
    isActive: raw.isActive,
    createdAt: raw.createdAt,
  };
}
